import './workout-sensor-details.css';
import { workoutDisplayDigits as displayDigits } from '../../utils/workoutNumberFormat';
import { paceFromKmh, formatRunningPace } from '../../utils/runningWorkoutMetrics';
import React, { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoCloseOutline } from 'react-icons/io5';
import { usePinchZoom } from '../../hooks/usePinchZoom';
import {
  normalizeWorkoutStreamTimeline,
  type StructureTimelineSegment,
} from '../../utils/workoutStructure';
import {
  buildSmoothedStream,
  parseStreamData,
  smoothingWindow,
  type StreamPoint,
} from '../../utils/workoutStream';
import { secToHMS } from '../../utils/secToHMS';

const STRUCTURE_FILL = 'rgba(223, 32, 99, 0.34)';
const STRUCTURE_STROKE = 'rgba(223, 32, 99, 0.5)';

/** Map elapsed seconds to the nearest stream index for overlay X alignment. */
function streamIndexAtTime(stream: StreamPoint[], timeSec: number): number {
  if (!stream.length) return 0;
  let idx = 0;
  for (let i = 0; i < stream.length; i++) {
    if (stream[i].timeSeconds <= timeSec) idx = i;
    else break;
  }
  return idx;
}

export type StreamSeriesKey = 'power' | 'hr' | 'cadence' | 'speed' | 'pace' | 'elevation' | 'temp';

/** Hold still to toggle selection mode; drag in that mode creates a range. Move = scrub. */
const LONG_PRESS_MS = 600;
const LONG_PRESS_MOVE_CANCEL_PX = 18;
const SELECT_DRAG_START_PX = 8;
const HOLD_RING_R = 22;

const SERIES_COLORS: Record<StreamSeriesKey, string> = {
  power: '#da17d8',
  hr: '#9d3e41',
  cadence: '#ebb550',
  speed: '#237e0d',
  pace: '#38bdf8',
  elevation: '#929292',
  temp: '#2011c9',
};

const SERIES_LABEL_KEYS: Record<StreamSeriesKey, string> = {
  power: 'workoutDetail.map.seriesPower',
  hr: 'workoutDetail.map.seriesHr',
  cadence: 'workoutDetail.map.seriesCadence',
  speed: 'workoutDetail.map.seriesSpeed',
  pace: 'runningMetrics.paceLabel',
  elevation: 'workoutDetail.map.seriesElevation',
  temp: 'workoutDetail.map.seriesTemp',
};

function seriesValue(pt: StreamPoint, key: StreamSeriesKey): number | null {
  switch (key) {
    case 'power':
      return pt.power;
    case 'hr':
      return pt.heartRate;
    case 'cadence':
      return pt.cadence;
    case 'speed':
      return pt.speed;
    case 'pace':
      return pt.speed != null && Number.isFinite(pt.speed) && pt.speed > 0 ? 3600 / pt.speed : null;
    case 'elevation':
      return pt.altitude;
    default:
      return null;
  }
}

function formatSeriesValue(key: StreamSeriesKey, v: number): string {
  switch (key) {
    case 'power':
      return `${Math.round(v)} W`;
    case 'hr':
      return `${Math.round(v)} bpm`;
    case 'cadence':
      return `${Math.round(v)} rpm`;
    case 'speed':
      return `${v.toFixed(1)} km/h`;
    case 'elevation':
      return `${Math.round(v)} m`;
    default:
      return String(Math.round(v));
  }
}

function normalizeY(key: StreamSeriesKey, v: number): number {
  switch (key) {
    case 'power':
      return Math.max(0, Math.min(520, v)) / 520;
    case 'hr':
      return (Math.max(55, Math.min(195, v)) - 55) / 140;
    case 'cadence':
      return Math.max(0, Math.min(155, v)) / 155;
    case 'speed':
      return Math.max(0, Math.min(72, v)) / 72;
    case 'elevation':
      return 0.5;
    case 'temp':
      return 0.5;
    default:
      return 0;
  }
}

export type MobileWorkoutStreamChartHandle = {
  zoomIn: () => void;
  resetZoom: () => void;
  zoomToSelection: (startIndex: number, endIndex: number) => void;
};

export const MobileWorkoutStreamChart = forwardRef(function MobileWorkoutStreamChart({
  rawStream,
  running = false,
  visible,
  smoothLevel = 30,
  selection,
  selectionMode = false,
  onSelectionChange,
  onClearSelection,
  onSelectionModeChange,
  onHoverIndex,
  showStructure = false,
  structureSegments = [],
  totalTime = 0,
  ftp = null,
  structureOffsetSec = 0,
  onZoomChange,
}: {
  rawStream: unknown;
  running?: boolean;
  visible: Record<StreamSeriesKey, boolean>;
  smoothLevel?: number;
  selection: { start: number; end: number } | null;
  selectionMode?: boolean;
  onSelectionChange: (sel: { start: number; end: number } | null) => void;
  onClearSelection?: () => void;
  onSelectionModeChange?: (active: boolean) => void;
  onHoverIndex?: (index: number | null) => void;
  /** Faded planned-structure overlay behind sensor lines. */
  showStructure?: boolean;
  structureSegments?: StructureTimelineSegment[];
  totalTime?: number;
  ftp?: number | null;
  /** Horizontal time shift (seconds) applied to structure blocks only. */
  structureOffsetSec?: number;
  onZoomChange?: (isZoomed: boolean) => void;
}, ref: React.Ref<MobileWorkoutStreamChartHandle>) {
  const { t, i18n } = useTranslation();
  const digits = (value: string | number) => displayDigits(value, i18n.resolvedLanguage ?? i18n.language);
  const interactionHintId = useId();
  const stream = useMemo(() => parseStreamData(rawStream), [rawStream]);
  const timelineStream = useMemo(
    () => normalizeWorkoutStreamTimeline(stream, totalTime),
    [stream, totalTime],
  );
  const win = useMemo(() => smoothingWindow(smoothLevel, timelineStream.length), [smoothLevel, timelineStream.length]);
  const chartStream = useMemo(() => buildSmoothedStream(timelineStream, win), [timelineStream, win]);

  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 320, h: 180 });
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [dragSel, setDragSel] = useState<{ start: number; end: number } | null>(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [holdRing, setHoldRing] = useState<{ x: number; y: number; progress: number } | null>(null);

  const [zoomStartFrac, setZoomStartFrac] = useState(0);
  const [zoomEndFrac, setZoomEndFrac] = useState(1);

  const activePointerId = useRef<number | null>(null);
  const pointerStartRef = useRef({ x: 0, y: 0 });
  const dragStartIdx = useRef(0);
  const isDragSelectRef = useRef(false);
  const pinchActiveRef = useRef(false);
  const selectionModeRef = useRef(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressRafRef = useRef<number | null>(null);
  const longPressStartMsRef = useRef(0);
  const longPressFiredRef = useRef(false);
  const longPressEligibleRef = useRef(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 40 && height > 40) setSize({ w: Math.round(width), h: Math.round(height) });
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    setZoomStartFrac(0);
    setZoomEndFrac(1);
  }, [rawStream]);

  const N = chartStream.length;
  const showPaceAxis = running && visible.pace;
  const ML = showPaceAxis ? 70 : 4;
  const MR = 4;
  // Keep the interactive HUD and the bottom guide out of the sensor traces.
  // The chart container is taller, so these safe lanes do not reduce the
  // useful plotting area compared with the compact version.
  const MT = 46;
  const MB = 30;
  const CW = Math.max(1, size.w - ML - MR);
  const CH = Math.max(1, size.h - MT - MB);
  const zoomSpan = zoomEndFrac - zoomStartFrac;
  const isZoomed = zoomStartFrac > 0.001 || zoomEndFrac < 0.999;

  const visRange = useMemo(() => {
    const si = Math.max(0, Math.floor(zoomStartFrac * (N - 1)));
    const ei = Math.min(N - 1, Math.ceil(zoomEndFrac * (N - 1)));
    return { start: si, end: ei };
  }, [zoomStartFrac, zoomEndFrac, N]);

  const elevationBounds = useMemo(() => {
    const values: number[] = [];
    for (let i = visRange.start; i <= visRange.end; i++) {
      const altitude = chartStream[i]?.altitude;
      if (altitude != null && Number.isFinite(altitude)) values.push(altitude);
    }
    if (!values.length) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    return { min, max, span: Math.max(max - min, 1) };
  }, [chartStream, visRange]);

  const paceBounds = useMemo(() => {
    const values = chartStream.slice(visRange.start, visRange.end + 1)
      .map(point => seriesValue(point, 'pace'))
      .filter((value): value is number => value != null && Number.isFinite(value));
    if (!values.length) return null;
    const min = Math.floor(Math.min(...values) / 30) * 30;
    const max = Math.max(min + 60, Math.ceil(Math.max(...values) / 30) * 30);
    return { min, span: max - min };
  }, [chartStream, visRange]);

  const normalizedSeriesY = useCallback(
    (key: StreamSeriesKey, value: number) => {
      if (key === 'pace' && paceBounds) return 0.1 + (1 - Math.max(0, Math.min(1, (value - paceBounds.min) / paceBounds.span))) * 0.8;
      if (key !== 'elevation' || !elevationBounds) return normalizeY(key, value);
      const normalized = (value - elevationBounds.min) / elevationBounds.span;
      return 0.08 + Math.max(0, Math.min(1, normalized)) * 0.78;
    },
    [elevationBounds, paceBounds],
  );

  const Xi = useCallback(
    (i: number) => {
      const frac = i / Math.max(N - 1, 1);
      const screenFrac = (frac - zoomStartFrac) / zoomSpan;
      return ML + screenFrac * CW;
    },
    [ML, CW, N, zoomStartFrac, zoomSpan],
  );

  const indexFromClientX = useCallback(
    (clientX: number) => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect || N < 2) return 0;
      const screenFrac = Math.max(0, Math.min(1, (clientX - rect.left - ML) / CW));
      const absFrac = zoomStartFrac + screenFrac * zoomSpan;
      return Math.round(absFrac * (N - 1));
    },
    [ML, CW, N, zoomStartFrac, zoomSpan],
  );

  const absFracFromClientX = useCallback(
    (clientX: number) => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect || N < 2) return 0;
      const screenFrac = Math.max(0, Math.min(1, (clientX - rect.left - ML) / CW));
      return zoomStartFrac + screenFrac * zoomSpan;
    },
    [ML, CW, N, zoomStartFrac, zoomSpan],
  );

  const zoomIn = useCallback(() => {
    const centerFrac = (zoomStartFrac + zoomEndFrac) / 2;
    const newSpan = Math.max(0.002, zoomSpan * 0.55);
    let newStart = centerFrac - newSpan / 2;
    let newEnd = centerFrac + newSpan / 2;
    if (newStart < 0) {
      newEnd += -newStart;
      newStart = 0;
    }
    if (newEnd > 1) {
      newStart -= newEnd - 1;
      newEnd = 1;
    }
    setZoomStartFrac(Math.max(0, newStart));
    setZoomEndFrac(Math.min(1, newEnd));
  }, [zoomStartFrac, zoomEndFrac, zoomSpan]);

  const resetZoom = useCallback(() => {
    setZoomStartFrac(0);
    setZoomEndFrac(1);
  }, []);

  const zoomToSelection = useCallback((startIndex: number, endIndex: number) => {
    if (N < 2) return;
    const a = Math.max(0, Math.min(Math.min(startIndex, endIndex), N - 1));
    const b = Math.max(0, Math.min(Math.max(startIndex, endIndex), N - 1));
    if (a === b) return;
    const padding = Math.max(2, Math.round((b - a) * 0.08));
    setZoomStartFrac(Math.max(0, (a - padding) / (N - 1)));
    setZoomEndFrac(Math.min(1, (b + padding) / (N - 1)));
  }, [N]);

  useImperativeHandle(
    ref,
    () => ({ zoomIn, resetZoom, zoomToSelection }),
    [zoomIn, resetZoom, zoomToSelection],
  );

  useEffect(() => {
    onZoomChange?.(isZoomed);
  }, [isZoomed, onZoomChange]);

  const applyPinchZoom = useCallback(
    (factor: number, centerClientX: number) => {
      const frac = absFracFromClientX(centerClientX);
      const span = zoomSpan;
      let newSpan = Math.max(0.002, Math.min(1, span / factor));
      const ratio = span > 0 ? (frac - zoomStartFrac) / span : 0.5;
      let newStart = frac - ratio * newSpan;
      let newEnd = newStart + newSpan;
      if (newStart < 0) {
        newEnd += -newStart;
        newStart = 0;
      }
      if (newEnd > 1) {
        newStart -= newEnd - 1;
        newEnd = 1;
      }
      setZoomStartFrac(Math.max(0, newStart));
      setZoomEndFrac(Math.min(1, newEnd));
    },
    [absFracFromClientX, zoomSpan, zoomStartFrac],
  );

  usePinchZoom(wrapRef, applyPinchZoom, N > 1);

  const cancelSelectDrag = useCallback(() => {
    isDragSelectRef.current = false;
    setDragSel(null);
  }, []);

  const toggleSelectionMode = useCallback(() => {
    const next = !selectionModeRef.current;
    selectionModeRef.current = next;
    if (!next) cancelSelectDrag();
    onSelectionModeChange?.(next);
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(12);
    }
  }, [cancelSelectDrag, onSelectionModeChange]);

  useEffect(() => {
    selectionModeRef.current = selectionMode;
  }, [selectionMode]);

  const clearLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (longPressRafRef.current != null) {
      cancelAnimationFrame(longPressRafRef.current);
      longPressRafRef.current = null;
    }
    setHoldRing(null);
  }, []);

  const startLongPress = useCallback(
    (clientX: number, clientY: number) => {
      clearLongPress();
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = clientX - rect.left;
      const y = clientY - rect.top;
      longPressStartMsRef.current = Date.now();
      longPressFiredRef.current = false;
      longPressEligibleRef.current = true;

      const tick = () => {
        if (!longPressEligibleRef.current) return;
        const elapsed = Date.now() - longPressStartMsRef.current;
        const progress = Math.min(1, elapsed / LONG_PRESS_MS);
        setHoldRing({ x, y, progress });
        if (progress < 1) {
          longPressRafRef.current = requestAnimationFrame(tick);
        }
      };
      longPressRafRef.current = requestAnimationFrame(tick);

      longPressTimerRef.current = setTimeout(() => {
        longPressTimerRef.current = null;
        if (!longPressEligibleRef.current) return;
        longPressFiredRef.current = true;
        longPressEligibleRef.current = false;
        setHoldRing(null);
        toggleSelectionMode();
      }, LONG_PRESS_MS);
    },
    [clearLongPress, toggleSelectionMode],
  );

  useEffect(() => () => clearLongPress(), [clearLongPress]);

  useEffect(() => {
    if (selection) return;
    cancelSelectDrag();
  }, [selection, cancelSelectDrag]);

  const prevSelectionRef = useRef(selection);
  useEffect(() => {
    if (prevSelectionRef.current && !selection) {
      selectionModeRef.current = false;
      onSelectionModeChange?.(false);
    }
    prevSelectionRef.current = selection;
  }, [selection, onSelectionModeChange]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || N < 2) return;

    const onTouchChange = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        pinchActiveRef.current = true;
        cancelSelectDrag();
        clearLongPress();
        activePointerId.current = null;
        setIsScrubbing(false);
      } else if (e.touches.length === 0) {
        pinchActiveRef.current = false;
      }
    };

    el.addEventListener('touchstart', onTouchChange, { passive: true });
    el.addEventListener('touchend', onTouchChange, { passive: true });
    el.addEventListener('touchcancel', onTouchChange, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchChange);
      el.removeEventListener('touchend', onTouchChange);
      el.removeEventListener('touchcancel', onTouchChange);
    };
  }, [N, cancelSelectDrag, clearLongPress]);

  const updateHover = useCallback(
    (idx: number | null) => {
      setHoverIdx(idx);
      onHoverIndex?.(idx);
    },
    [onHoverIndex],
  );

  const paths = useMemo(() => {
    const out: Partial<Record<StreamSeriesKey, string>> = {};
    const { start, end } = visRange;
    (Object.keys(SERIES_COLORS) as StreamSeriesKey[]).forEach((key) => {
      if (!visible[key] || (key === 'pace' && !running)) return;
      let d = '';
      let drawing = false;
      for (let i = start; i <= end; i++) {
        const v = seriesValue(chartStream[i], key);
        if (v == null || !Number.isFinite(v)) {
          drawing = false;
          continue;
        }
        const x = Xi(i).toFixed(1);
        const y = (MT + CH - normalizedSeriesY(key, v) * CH).toFixed(1);
        d += drawing ? ` L${x},${y}` : `M${x},${y}`;
        drawing = true;
      }
      out[key] = d;
    });
    return out;
  }, [chartStream, visible, Xi, CH, MT, normalizedSeriesY, visRange, running]);

  const elevationAreaPath = useMemo(() => {
    if (!visible.elevation || !paths.elevation) return '';
    const points: Array<{ x: number; y: number }> = [];
    for (let i = visRange.start; i <= visRange.end; i++) {
      const altitude = seriesValue(chartStream[i], 'elevation');
      if (altitude == null || !Number.isFinite(altitude)) continue;
      points.push({
        x: Xi(i),
        y: MT + CH - normalizedSeriesY('elevation', altitude) * CH,
      });
    }
    if (points.length < 2) return '';
    const baseline = MT + CH;
    return [
      `M${points[0].x.toFixed(1)},${baseline.toFixed(1)}`,
      ...points.map((point) => `L${point.x.toFixed(1)},${point.y.toFixed(1)}`),
      `L${points[points.length - 1].x.toFixed(1)},${baseline.toFixed(1)} Z`,
    ].join(' ');
  }, [CH, MT, Xi, chartStream, normalizedSeriesY, paths.elevation, visRange, visible.elevation]);

  const Yp = useCallback(
    (watts: number) => MT + CH - normalizeY('power', watts) * CH,
    [MT, CH],
  );

  const timeToXi = useCallback(
    (timeSec: number) => Xi(streamIndexAtTime(timelineStream, timeSec)),
    [Xi, timelineStream],
  );

  const structureShapes = useMemo(() => {
    if (!showStructure || !structureSegments.length || N < 2) return [];
    const ftpRef = ftp && ftp > 0 ? ftp : 250;
    const pctToWatts = (pct: number) => Math.max(0, Math.min(520, (ftpRef * pct) / 100));
    const yBase = MT + CH;
    const clampY = (y: number) => Math.max(MT, Math.min(yBase, y));

    return structureSegments.map((seg) => {
      const x1 = timeToXi(seg.startSec + structureOffsetSec);
      const x2 = timeToXi(seg.endSec + structureOffsetSec);
      const w = Math.max(x2 - x1, 1);
      const wMin = pctToWatts(seg.minPct);
      const wMax = pctToWatts(seg.maxPct);

      if (seg.isRamp) {
        const growing = seg.minPct <= seg.maxPct;
        const yLeft = clampY(Yp(growing ? wMin : wMax));
        const yRight = clampY(Yp(growing ? wMax : wMin));
        const pts = `${x1.toFixed(1)},${yBase} ${x1.toFixed(1)},${yLeft.toFixed(1)} ${x2.toFixed(1)},${yRight.toFixed(1)} ${x2.toFixed(1)},${yBase}`;
        return (
          <polygon
            key={`struct-${seg.index}`}
            points={pts}
            fill={STRUCTURE_FILL}
            stroke={STRUCTURE_STROKE}
            strokeWidth={0.8}
          />
        );
      }

      const yTop = clampY(Yp(Math.max(wMin, wMax)));
      const yBot = clampY(Yp(Math.min(wMin, wMax)));
      const barH = Math.max(yBot - yTop, 2);
      return (
        <rect
          key={`struct-${seg.index}`}
          x={x1 + 0.5}
          y={yTop}
          width={Math.max(w - 1, 1)}
          height={barH}
          rx={Math.min(2, Math.max(w - 1, 1) / 2)}
          fill={STRUCTURE_FILL}
          stroke={STRUCTURE_STROKE}
          strokeWidth={0.8}
        />
      );
    });
  }, [showStructure, structureSegments, structureOffsetSec, N, ftp, timeToXi, Yp, MT, CH]);

  const tryStartSelectDrag = (clientX: number) => {
    if (!selectionModeRef.current || isDragSelectRef.current) return;
    const idx = indexFromClientX(clientX);
    isDragSelectRef.current = true;
    dragStartIdx.current = idx;
    setDragSel({ start: idx, end: idx });
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (N < 2 || pinchActiveRef.current) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    activePointerId.current = e.pointerId;
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    setIsScrubbing(true);

    const idx = indexFromClientX(e.clientX);
    updateHover(idx);

    if (e.pointerType === 'touch' || e.pointerType === 'pen') {
      startLongPress(e.clientX, e.clientY);
    }

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* capture unsupported */
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (N < 2) return;

    if (e.pointerType === 'mouse' && e.buttons === 0) {
      updateHover(indexFromClientX(e.clientX));
      return;
    }

    if (activePointerId.current !== e.pointerId || pinchActiveRef.current) return;

    const dx = e.clientX - pointerStartRef.current.x;
    const dy = e.clientY - pointerStartRef.current.y;
    const moved = Math.hypot(dx, dy);

    if (longPressEligibleRef.current && moved >= LONG_PRESS_MOVE_CANCEL_PX) {
      longPressEligibleRef.current = false;
      clearLongPress();
    }

    const idx = indexFromClientX(e.clientX);
    updateHover(idx);

    if (
      selectionModeRef.current
      && !isDragSelectRef.current
      && Math.abs(dx) >= SELECT_DRAG_START_PX
      && Math.abs(dx) >= Math.abs(dy)
    ) {
      tryStartSelectDrag(pointerStartRef.current.x);
    }

    if (isDragSelectRef.current) {
      const start = dragStartIdx.current;
      setDragSel({ start, end: idx });
      onSelectionChange({ start, end: idx });
    }
  };

  const finishSelectDrag = (clientX: number) => {
    if (!isDragSelectRef.current) return;
    isDragSelectRef.current = false;
    const idx = indexFromClientX(clientX);
    const a = Math.min(dragStartIdx.current, idx);
    const b = Math.max(dragStartIdx.current, idx);
    setDragSel(null);
    if (a !== b) {
      onSelectionChange({ start: a, end: b });
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (activePointerId.current !== e.pointerId) return;

    const wasLongPress = longPressFiredRef.current;
    longPressEligibleRef.current = false;
    clearLongPress();

    if (isDragSelectRef.current) {
      finishSelectDrag(e.clientX);
    }

    if (!wasLongPress) {
      updateHover(indexFromClientX(e.clientX));
    }

    longPressFiredRef.current = false;
    activePointerId.current = null;
    setIsScrubbing(false);

    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    toggleSelectionMode();
  };

  const onPointerCancel = () => {
    cancelSelectDrag();
    longPressEligibleRef.current = false;
    longPressFiredRef.current = false;
    clearLongPress();
    activePointerId.current = null;
    setIsScrubbing(false);
  };

  const activeSel = dragSel ?? selection;
  const selBand = useMemo(() => {
    if (!activeSel || N < 2) return null;
    const a = Math.min(activeSel.start, activeSel.end);
    const b = Math.max(activeSel.start, activeSel.end);
    if (a === b) return null;
    return { x1: Xi(a), x2: Xi(b) };
  }, [activeSel, N, Xi]);

  const hoverPt = hoverIdx != null ? chartStream[hoverIdx] : null;
  const hoverX = hoverIdx != null ? Xi(hoverIdx) : null;

  const tooltipRows = useMemo(() => {
    if (!hoverPt) return [];
    const rows = (Object.keys(SERIES_COLORS) as StreamSeriesKey[])
      .filter((key) => visible[key] && (key !== 'pace' || running))
      .map((key) => {
        const v = seriesValue(hoverPt, key);
        if (v == null || !Number.isFinite(v)) return null;
        return {
          key,
          label: t(key === 'cadence' && running ? 'runningMetrics.cadence' : SERIES_LABEL_KEYS[key]),
          value: key === 'pace' ? `${formatRunningPace(v)} ${t('runningMetrics.paceUnit')}` : key === 'cadence' && running ? `${Math.round(v)} ${t('runningMetrics.cadenceUnit')}` : formatSeriesValue(key, v),
          color: SERIES_COLORS[key],
        };
      })
      .filter(Boolean) as Array<{ key: StreamSeriesKey; label: string; value: string; color: string }>;
    if (running && !visible.pace && visible.speed && hoverPt.speed != null && Number.isFinite(hoverPt.speed)) {
      rows.push({ key: 'speed', label: t('runningMetrics.paceLabel'), value: `${paceFromKmh(hoverPt.speed)} ${t('runningMetrics.paceUnit')}`, color: SERIES_COLORS.speed });
    }
    return rows;
  }, [hoverPt, visible, t, running]);

  const hoverDots = useMemo(() => {
    if (hoverIdx == null) return [];
    const pt = chartStream[hoverIdx];
    if (!pt) return [];
    return (Object.keys(SERIES_COLORS) as StreamSeriesKey[])
      .filter((key) => visible[key] && (key !== 'pace' || running))
      .map((key) => {
        const v = seriesValue(pt, key);
        if (v == null || !Number.isFinite(v)) return null;
        return {
          key,
          cx: Xi(hoverIdx),
          cy: MT + CH - normalizedSeriesY(key, v) * CH,
          color: SERIES_COLORS[key],
        };
      })
      .filter(Boolean) as Array<{ key: StreamSeriesKey; cx: number; cy: number; color: string }>;
  }, [hoverIdx, chartStream, visible, Xi, CH, MT, normalizedSeriesY, running]);

  const selectedTimeLabel = useMemo(() => {
    if (!selection || !timelineStream.length) return null;
    const a = Math.max(0, Math.min(selection.start, selection.end, timelineStream.length - 1));
    const b = Math.max(0, Math.min(Math.max(selection.start, selection.end), timelineStream.length - 1));
    return `${secToHMS(timelineStream[a]?.timeSeconds ?? 0)} – ${secToHMS(timelineStream[b]?.timeSeconds ?? 0)}`;
  }, [selection, timelineStream]);

  const visStartTime = chartStream[visRange.start]?.timeSeconds ?? 0;
  const visEndTime = chartStream[visRange.end]?.timeSeconds ?? chartStream[N - 1]?.timeSeconds ?? 0;

  const handleWheel = (e: React.WheelEvent) => {
    if (N < 2) return;
    e.preventDefault();
    applyPinchZoom(e.deltaY < 0 ? 1 / 0.85 : 0.85, e.clientX);
  };

  return (
    <div
      ref={wrapRef}
      className={`wm-stream-chart ltr-island chart-touch-plot pinch-zoom-zone${isScrubbing ? ' chart-touch-plot--scrubbing' : ''}${selectionMode ? ' wm-stream-chart--select-mode' : ''}${holdRing ? ' wm-stream-chart--holding' : ''}`}
      onWheel={handleWheel}
      role="group"
      aria-label={t('workoutDetail.map.chartInteractionLabel')}
      aria-describedby={interactionHintId}
    >
      {N < 2 ? (
        <div className="wm-stream-chart__empty">—</div>
      ) : (
        <>
          {selection ? (
            <button
              id={interactionHintId}
              type="button"
              className="wm-stream-chart__selection-close wm-stream-chart__selection-close--on-band"
              style={{
                left: selBand
                  ? Math.max(52, Math.min(size.w - 52, selBand.x2 - 14))
                  : size.w / 2,
              }}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={onClearSelection}
              aria-label={`${t('workoutDetail.map.clearSelection')} — ${selectedTimeLabel == null ? '' : digits(selectedTimeLabel)}`}
              title={t('workoutDetail.map.clearSelection')}
            >
              <IoCloseOutline aria-hidden />
            </button>
          ) : !selectionMode ? (
            <div id={interactionHintId} className="wm-stream-chart__gesture-hint ltr-island" role="note">
              {t('workoutDetail.map.holdForSelection')}
            </div>
          ) : (
            <div id={interactionHintId} className="wm-stream-chart__select-hint" aria-live="polite">
              <span>{t('workoutDetail.map.selectionModeHint')}</span>
            </div>
          )}
          {holdRing ? (
            <div
              className="wm-stream-chart__hold-ring"
              style={{ left: holdRing.x, top: holdRing.y }}
              aria-hidden
            >
              <svg width={HOLD_RING_R * 2} height={HOLD_RING_R * 2} viewBox="0 0 44 44">
                <circle
                  className="wm-stream-chart__hold-ring-track"
                  cx="22"
                  cy="22"
                  r="18"
                  fill="none"
                  strokeWidth="3"
                />
                <circle
                  className="wm-stream-chart__hold-ring-progress"
                  cx="22"
                  cy="22"
                  r="18"
                  fill="none"
                  strokeWidth="3"
                  strokeDasharray={`${holdRing.progress * 113.1} 113.1`}
                  transform="rotate(-90 22 22)"
                />
              </svg>
            </div>
          ) : null}
          <svg width={size.w} height={size.h} className="wm-stream-chart__svg">
            <defs>
              <clipPath id="wm-stream-chart-clip">
                <rect x={ML} y={MT} width={CW} height={CH} />
              </clipPath>
            </defs>
            {showPaceAxis && paceBounds ? (
              <g className="wm-stream-chart__pace-axis" pointerEvents="none">
                <text x={12} y={MT + CH / 2} transform={`rotate(-90 12 ${MT + CH / 2})`} fill="var(--fg-2)" fontSize="10" direction="rtl" textAnchor="middle" dominantBaseline="middle">{t('runningMetrics.paceUnit')}</text>
                <line x1={ML} x2={ML} y1={MT} y2={MT + CH} stroke="var(--fg-4)" />
                {Array.from({ length: paceBounds.span / 30 + 1 }, (_, index) => {
                  const seconds = paceBounds.min + index * 30;
                  const y = MT + CH - normalizedSeriesY('pace', seconds) * CH;
                  return <g key={index}>
                    <line x1={ML} x2={size.w - MR} y1={y} y2={y} stroke="var(--fg-4)" strokeOpacity="0.25" strokeDasharray="3 4" />
                    <text x={ML - 8} y={y} fill="var(--fg-2)" fontSize="11" textAnchor="end" dominantBaseline="middle" direction="ltr">{digits(formatRunningPace(seconds))}</text>
                  </g>;
                })}
              </g>
            ) : null}
            <g clipPath="url(#wm-stream-chart-clip)">{structureShapes}</g>
            {elevationAreaPath ? (
              <path
                className="wm-stream-chart__elevation-area"
                d={elevationAreaPath}
                fill={SERIES_COLORS.elevation}
              />
            ) : null}
            {selBand ? (
              <rect
                x={selBand.x1}
                y={MT}
                width={Math.max(1, selBand.x2 - selBand.x1)}
                height={CH}
                fill="rgba(30, 144, 255, 0.26)"
                stroke="rgba(93, 188, 255, 0.95)"
                strokeWidth={1.5}
              />
            ) : null}
            {(Object.keys(paths) as StreamSeriesKey[]).map((key) =>
              paths[key] ? (
                <path
                  key={key}
                  d={paths[key]}
                  fill="none"
                  stroke={SERIES_COLORS[key]}
                  strokeWidth={key === 'elevation' ? 1.25 : 1.8}
                  opacity={key === 'elevation' ? 0.52 : 1}
                />
              ) : null,
            )}
            {hoverX != null ? (
              <line
                x1={hoverX}
                y1={MT}
                x2={hoverX}
                y2={MT + CH}
                className="wm-stream-chart__cursor"
                strokeWidth={1.5}
              />
            ) : null}
            {hoverDots.map((dot) => (
              <circle
                key={dot.key}
                cx={dot.cx}
                cy={dot.cy}
                r={4}
                fill={dot.color}
                stroke="#fff"
                strokeWidth={1.5}
              />
            ))}
            <rect
              x={ML}
              y={MT}
              width={CW}
              height={CH}
              fill="transparent"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerCancel}
              onDoubleClick={onDoubleClick}
              onPointerLeave={() => {
                if (activePointerId.current == null) updateHover(null);
              }}
            />
            <text x={ML} y={size.h - 4} fontSize="9" fill="var(--fg-4)">
              {digits(secToHMS(visStartTime))}
            </text>
            <text x={ML + CW} y={size.h - 4} fontSize="9" fill="var(--fg-4)" textAnchor="end">
              {digits(secToHMS(visEndTime))}
            </text>
          </svg>

          {hoverPt && tooltipRows.length > 0 ? (
            <div className="wm-stream-chart__tooltip" aria-hidden>
              <div className="wm-stream-chart__tooltip-time ltr-island">{digits(secToHMS(hoverPt.timeSeconds))}</div>
              <div className="wm-stream-chart__tooltip-series" style={{ '--wm-sensor-count': tooltipRows.length } as React.CSSProperties}>
                {tooltipRows.map((row) => (
                  <div key={row.label} className="wm-stream-chart__tooltip-row" title={`${row.label}: ${digits(row.value)}`}>
                    <span className="wm-stream-chart__tooltip-dot" style={{ background: row.color }} />
                    <span className="wm-stream-chart__tooltip-value ltr-island">
                      <strong className="wm-stream-chart__tooltip-number">{digits(row.value.split(' ')[0])}</strong>
                      <small className="wm-stream-chart__tooltip-unit">{row.value.split(' ').slice(1).join(' ')}</small>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
});
